import { Customer } from "./../models/customer";
import { Article } from "./../models/article";
import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { map } from "rxjs/operators";
import { CartItem } from "../classes/cart-item";
import { getArticlePrice, round } from "../utils/utils";
import { TranslateService } from "@ngx-translate/core";
import { CustomSnackbarService } from "./custom.snackbar.service";
import { SnackbarTypeError } from "../msg-snackbar/msg-snackbar.component";
import { CryptoService } from "./crypto.service";
import { StorageService } from "./storage.service";

/**
 * Modelo seguro del carrito con precio garantizado
 */
export interface SecureCartItem {
  sku: string; // SKU del producto
  quantity: number; // Cantidad
  priceSnapshot: number; // Precio al momento de agregarlo (garantizado)
  addedAt: number; // Timestamp cuando se agregó (para validar garantía)
}

/**
 * Modelo extendido para mostrar en UI (con datos del producto)
 */
export interface CartItemDisplay extends SecureCartItem {
  product?: Article; // Producto completo (se carga bajo demanda)
  isPriceGuaranteed?: boolean; // Si el precio está garantizado
  timeRemaining?: string; // Tiempo restante de garantía
}

@Injectable({
  providedIn: "root",
})
export class CartService {
  // ID único para esta instancia del servicio
  private readonly instanceId = Math.random().toString(36).substring(7);

  // Constantes
  private readonly CART_KEY = "bhc"; // BuHo Cart
  private readonly CHECKSUM_KEY = "bhc_cs"; // Checksum
  private readonly PRICE_GUARANTEE_TIME = 2 * 60 * 60 * 1000; // 2 horas en ms

  // BehaviorSubject para compatibilidad con código existente
  public cartItems: BehaviorSubject<SecureCartItem[]> = new BehaviorSubject([]);
  public observer: Subscriber<{}>;

  constructor(
    private toastrService: ToastrService,
    private translate: TranslateService,
    private snackBar: CustomSnackbarService,
    private cryptoService: CryptoService,
    private storageService: StorageService
  ) {

    // Configurar almacenamiento en cookies (cambiar a 'localStorage' si prefieres)
    this.storageService.setStorageType("cookie");

    // Cargar carrito al iniciar
    this.loadCart();
  }

  // ==================== CARGA Y GUARDADO ====================

  /**
   * Carga el carrito desde almacenamiento encriptado
   * También migra datos antiguos si existen
   */
  private loadCart(): void {

    // PASO 1: Intentar cargar carrito encriptado (nuevo formato)
    const encryptedCart = this.storageService.get(this.CART_KEY);
    const checksum = this.storageService.get(this.CHECKSUM_KEY);

    if (encryptedCart && checksum) {
      // Desencriptar
      const decryptedCart = this.cryptoService.decrypt(encryptedCart);

      if (decryptedCart && Array.isArray(decryptedCart)) {
        // Verificar checksum para detectar manipulación
        if (this.cryptoService.verifyChecksum(decryptedCart, checksum)) {
          this.cartItems.next(decryptedCart);
          this.cleanOldLocalStorage(); // Limpiar localStorage viejo
          return;
        } else {
          console.warn(
            "⚠️ ALERTA: Carrito manipulado o corrupto. Limpiando carrito por seguridad."
          );
          this.clearCartStorage();
        }
      } else {
        console.warn("⚠️ No se pudo desencriptar el carrito. Limpiando...");
        this.clearCartStorage();
      }
    }

    // PASO 2: Si no hay carrito encriptado, intentar migrar desde localStorage antiguo
    this.migrateOldCart();
  }

  /**
   * Migra el carrito antiguo (localStorage sin encriptar) al nuevo formato
   */
  private migrateOldCart(): void {
    try {
      const oldCart = localStorage.getItem("cartItem");

      if (oldCart) {
        const oldCartItems: CartItem[] = JSON.parse(oldCart);
        const customer = this.getCustomer();
        const listPrice = customer ? customer.listPrice : 1;

        // Convertir a formato nuevo
        const newCart: SecureCartItem[] = oldCartItems.map((oldItem) => {
          const price = getArticlePrice(oldItem.product, listPrice);
          return {
            sku: oldItem.product.sku,
            quantity: oldItem.quantity,
            priceSnapshot: price,
            addedAt: Date.now(), // Timestamp actual
          };
        });

        // Guardar en formato encriptado
        this.saveCart(newCart);

        // Limpiar localStorage antiguo
        this.cleanOldLocalStorage();

      } else {
        // No hay carrito antiguo ni nuevo - carrito vacío
        this.cartItems.next([]);
      }
    } catch (error) {
      console.error("Error al migrar carrito antiguo:", error);
      this.cartItems.next([]);
      this.cleanOldLocalStorage();
    }
  }

  /**
   * Limpia el localStorage antiguo (cartItem, wishlistItem, compareItem)
   */
  private cleanOldLocalStorage(): void {
    // Limpiar carrito antiguo
    if (localStorage.getItem("cartItem")) {
      localStorage.removeItem("cartItem");
    }
  }

  /**
   * Guarda el carrito en almacenamiento encriptado
   */
  private saveCart(cart: SecureCartItem[]): void {

    // Encriptar datos
    const encrypted = this.cryptoService.encrypt(cart);

    // Generar checksum
    const checksum = this.cryptoService.generateChecksum(cart);

    if (encrypted && checksum) {
      // Guardar en cookies (o localStorage según configuración)
      this.storageService.set(this.CART_KEY, encrypted, 30); // 30 días
      this.storageService.set(this.CHECKSUM_KEY, checksum, 30);


      // Actualizar observable - emitir nuevo valor
      this.cartItems.next(cart);
    } else {
      console.error("❌ Error al encriptar el carrito");
      console.error("Encrypted:", !!encrypted, "Checksum:", !!checksum);
    }
  }

  /**
   * Limpia el almacenamiento del carrito
   */
  private clearCartStorage(): void {
    this.storageService.delete(this.CART_KEY);
    this.storageService.delete(this.CHECKSUM_KEY);
    this.cartItems.next([]);
  }

  // ==================== MÉTODOS PÚBLICOS ====================

  /**
   * Vaciar carrito (compatible con código existente)
   */
  public vaciarCarrito(): void {
    this.clearCartStorage();
    this.cleanOldLocalStorage(); // Limpiar también el localStorage antiguo
  }

  /**
   * Obtener items del carrito (compatible con código existente)
   * Retorna CartItem[] para compatibilidad
   */
  public getItems(): Observable<CartItem[]> {
    return this.cartItems.pipe(
      map((secureItems: SecureCartItem[]) => {
        // Convertir a formato antiguo para compatibilidad
        // Nota: El product será null, debe cargarse por separado
        return secureItems.map(
          (item) =>
            ({
              product: null, // Se carga en el componente
              quantity: item.quantity,
            } as CartItem)
        );
      })
    );
  }

  /**
   * Obtener items seguros del carrito (nuevo método)
   */
  public getSecureItems(): SecureCartItem[] {
    return this.cartItems.value;
  }

  /**
   * Obtener item del carrito (compatible)
   */
  getCartItem = (): CartItem[] => {
    const secureItems = this.cartItems.value;
    return secureItems.map(
      (item) =>
        ({
          product: null,
          quantity: item.quantity,
        } as CartItem)
    );
  };

  /**
   * Agregar al carrito (compatible, pero con priceSnapshot)
   */
  public addToCart(product: Article, quantity: number): CartItem | boolean {

    const cart = this.cartItems.value;
    const customer = this.getCustomer();
    const listPrice = customer ? customer.listPrice : 1;

    // Calcular precio actual
    const currentPrice = getArticlePrice(product, listPrice);

    // Buscar si el producto ya existe
    const existingItemIndex = cart.findIndex((item) => item.sku === product.sku);

    let newCart: SecureCartItem[];

    if (existingItemIndex !== -1) {
      // Producto existe: incrementar cantidad
      // NO actualizamos el priceSnapshot ni addedAt (mantener precio original)
      newCart = cart.map((item, index) =>
        index === existingItemIndex
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );

      this.translate.get("cart-added").subscribe((res) => {
        this.snackBar.open(res, SnackbarTypeError.INFO);
      });
    } else {
      // Producto nuevo: agregar con priceSnapshot
      const newItem: SecureCartItem = {
        sku: product.sku,
        quantity: quantity,
        priceSnapshot: currentPrice,
        addedAt: Date.now(),
      };
      newCart = [...cart, newItem];

      this.translate.get("cart-added").subscribe((res) => {
        this.snackBar.open(res, SnackbarTypeError.INFO);
      });
    }

    // Guardar carrito encriptado
    this.saveCart(newCart);

    // Retornar formato antiguo para compatibilidad
    return { product: product, quantity: quantity };
  }

  /**
   * Actualizar cantidad (compatible)
   */
  public updateCartQuantity(
    product: Article,
    quantity: number
  ): CartItem | boolean {
    const cart = this.cartItems.value;
    const item = cart.find((i) => i.sku === product.sku);

    if (item) {
      const newQty = Number(item.quantity) + Number(quantity);

      if (newQty <= 0) {
        // Eliminar si la cantidad es 0 o negativa
        this.removeFromCartBySku(product.sku);
      } else {
        // Crear una nueva copia del array para forzar la detección de cambios
        const newCart = cart.map(i =>
          i.sku === product.sku ? { ...i, quantity: newQty } : i
        );
        this.saveCart(newCart);
      }

      return true;
    }

    return false;
  }

  /**
   * Eliminar del carrito (compatible)
   */
  public removeFromCart(item: CartItem): boolean {
    if (!item) {
      console.error("❌ Item es null o undefined");
      return false;
    }
    if (!item.product) {
      console.error("❌ item.product es null o undefined");
      return false;
    }
    return this.removeFromCartBySku(item.product.sku);
  }

  /**
   * Eliminar del carrito por SKU
   */
  public removeFromCartBySku(sku: string): boolean {
    const cart = this.cartItems.value;
    const index = cart.findIndex((item) => item.sku === sku);

    if (index !== -1) {
      // Crear una nueva copia del array para forzar la detección de cambios
      const newCart = [...cart];
      newCart.splice(index, 1);
      this.saveCart(newCart);
      return true;
    }

    console.error("❌ No se encontró el item con SKU:", sku);
    return false;
  }

  /**
   * Calcular total (compatible)
   */
  public getTotalAmount(): Observable<number> {
    return this.cartItems.pipe(
      map((items: SecureCartItem[]) => {
        return items.reduce((total, item) => {
          // Usar priceSnapshot en lugar del precio actual
          return round(total + item.priceSnapshot * item.quantity);
        }, 0);
      })
    );
  }

  /**
   * Obtener precio de un item (compatible, usando priceSnapshot)
   */
  public getPrice(item: CartItem | SecureCartItem): number {
    // Si es SecureCartItem, usar priceSnapshot
    if ("priceSnapshot" in item) {
      return item.priceSnapshot;
    }

    // Si es CartItem antiguo, calcular precio normalmente
    if (item.product) {
      const customer = this.getCustomer();
      const listPrice = customer ? customer.listPrice : 1;
      return getArticlePrice(item.product, listPrice);
    }

    return 0;
  }

  // ==================== MÉTODOS DE GARANTÍA DE PRECIO ====================

  /**
   * Verifica si el precio de un item está garantizado (< 2 horas)
   */
  public isPriceGuaranteed(item: SecureCartItem): boolean {
    const age = Date.now() - item.addedAt;
    return age < this.PRICE_GUARANTEE_TIME;
  }

  /**
   * Obtiene el tiempo restante de garantía
   */
  public getTimeRemaining(item: SecureCartItem): string {
    const remaining = this.PRICE_GUARANTEE_TIME - (Date.now() - item.addedAt);

    if (remaining <= 0) {
      return "Expirado";
    }

    const minutes = Math.floor(remaining / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    } else {
      return `${mins}m`;
    }
  }

  /**
   * Actualiza el priceSnapshot de un item (cuando expira la garantía)
   */
  public updatePriceSnapshot(sku: string, newPrice: number): void {
    const cart = this.cartItems.value;
    const item = cart.find((i) => i.sku === sku);

    if (item) {
      item.priceSnapshot = newPrice;
      item.addedAt = Date.now(); // Resetear timestamp
      this.saveCart(cart);
    }
  }

  /**
   * Obtiene items con información de garantía
   */
  public getItemsWithGuaranteeInfo(): CartItemDisplay[] {
    return this.cartItems.value.map((item) => ({
      ...item,
      isPriceGuaranteed: this.isPriceGuaranteed(item),
      timeRemaining: this.getTimeRemaining(item),
    }));
  }

  // ==================== HELPERS ====================

  /**
   * Obtiene el cliente logueado
   */
  private getCustomer(): Customer | null {
    try {
      const identity = JSON.parse(localStorage.getItem("login"));
      if (identity && identity.customer) {
        return identity.customer;
      }
    } catch (error) {
      console.error("Error al obtener customer:", error);
    }
    return null;
  }

  /**
   * Obtiene el número total de items en el carrito
   */
  public getTotalItems(): number {
    const total = this.cartItems.value.reduce(
      (total, item) => total + item.quantity,
      0
    );
    return total;
  }

  /**
   * Calcula stock (método antiguo, mantenido para compatibilidad)
   */
  public calculateStockCounts(product: CartItem, quantity): CartItem | Boolean {
    // Stock validation disabled - mantener para compatibilidad
    return true;
  }
}